import React, { FC, useContext } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import styled, { css } from 'styled-components';

import DateTimeControl, { IntervalPart } from '../DateTimeControl';
import FormContext from 'src/presentation/contexts/FormContext';
import InputField from 'src/presentation/components/forms/InputField';
import RadioGroup from 'src/presentation/components/forms/RadioGroup';
import { ErrorLabel } from '@ad-scripts/presentation/components/common/forms/control_container';
import { Row } from 'src/presentation/components/forms/Layout';
import { ScheduleException } from 'src/models/BusinessHours/BusinessHours';
import { Table, TR, TBody } from '@ad-scripts/presentation/components/common/table';
import { exceptionIsDuplicated } from 'src/models/BusinessHours/validation/IsBusinessHoursExceptionUnique';

type BusinessHoursExceptionItem = {
  'data-aid': string;
  attr: string;
  baseAttr: string;
  index: number;
};

const BusinessHoursExceptionItem: FC<BusinessHoursExceptionItem> = ({ attr, baseAttr, 'data-aid': dataAid }) => {
  const { workingState, errors } = useContext(FormContext);

  let duplicateError = find(errors, error => error.code === 'isBusinessHoursExceptionUnique');
  if (duplicateError) {
    const item = get(workingState, attr) as ScheduleException;
    const exceptions = get(workingState, baseAttr) as ScheduleException[];

    if (!exceptionIsDuplicated(item, exceptions)) {
      duplicateError = null;
    }
  }

  const nameError = find(errors, error => error.attr === `${attr}.name`);
  const timeRangeError = find(errors, error => [`${attr}.start`, `${attr}.stop`].includes(error.attr));

  const renderErrorLine1 =
    nameError || duplicateError ? (
      <ErrorSection errorMessage={get(nameError, 'detail', get(duplicateError, 'detail'))} index={1} />
    ) : null;
  const renderErrorLine2 = timeRangeError ? (
    <ErrorSection errorMessage={get(timeRangeError, 'detail')} index={2} />
  ) : null;

  const openClosedRadioControl = (
    <StyledRadioGroup
      attr={`${attr}.closed`}
      data-aid={`${dataAid}-closed}`}
      options={[
        { label: 'We are open during this time', value: false },
        { label: 'We are closed during this time', value: true },
      ]}
    />
  );

  return (
    <>
      <ItemContainer data-aid={dataAid} className="bh-item-container" hasError={!!duplicateError}>
        <TBody>
          <TR>
            <TD>
              <StyledInputField
                className="name-input"
                placeholder="i.e. holidays, company holiday, etc."
                attr={`${attr}.name`}
                data-aid="name"
              />
            </TD>
          </TR>
          <TR>
            <ShortTD>
              <StyledRow>
                <DateTimeControl
                  attr={`${attr}.start`}
                  data-aid="start"
                  intervalPart={IntervalPart.START}
                  isInvalid={!!duplicateError}
                />
                <DateTimeSplitter>to</DateTimeSplitter>
                <DateTimeControl
                  attr={`${attr}.stop`}
                  data-aid="stop"
                  intervalPart={IntervalPart.END}
                  isInvalid={!!duplicateError}
                />
              </StyledRow>
            </ShortTD>
          </TR>
          {renderErrorLine1}
          {renderErrorLine2}
          <TR>
            <TD>{openClosedRadioControl}</TD>
          </TR>
        </TBody>
      </ItemContainer>
    </>
  );
};

const ErrorSection = ({ errorMessage, index }) => (
  <TR>
    <TD colSpan={2}>
      <ErrorLabel data-aid={`error-line-${index}`}>{errorMessage}</ErrorLabel>
    </TD>
  </TR>
);

const TD = styled.td`
  padding: 4px 0px;
`;

const ShortTD = styled(TD)`
  padding-right: 70px;
`;

const DateTimeSplitter = styled.span`
  margin-right: 0 !important;
  padding: 8px;
  text-align: center;
`;

const StyledRadioGroup = styled(RadioGroup)`
  flex-direction: row;

  & > label {
    flex-basis: auto;

    &:not(:last-child) {
      margin-right: 40px;
    }
  }
`;

const StyledRow = styled(Row)`
  align-items: center;
  margin-bottom: 0;
`;

const StyledInputField = styled(InputField)`
  ${ErrorLabel} {
    display: none;
  }
`;

const StyleInvalidStyledInputField = css`
  ${StyledInputField} {
    input {
      border-color: ${p => p.theme.colors.red400};
    }
  }
`;

const ItemContainer = styled(Table)`
  border: 0;

  ${p => (p.hasError ? StyleInvalidStyledInputField : '')}
`;

export default BusinessHoursExceptionItem;
